import { useEffect, useMemo, useState } from "react";
import {
  BOOKING_STATUSES,
  STATUS_META,
  formatBookingDate,
  formatDateTime,
  statusMeta,
  typeLabel,
  type Booking,
  type BookingStatus,
} from "@/lib/bookings";
import { supabase } from "@/lib/supabase";
import { useAssetUrlMap } from "@/hooks/useAssetUrlMap";
import { isStoragePath } from "@/lib/mediaStorage";

export type NudgeResult = "sent" | "skipped" | "error";

interface BookingDetailPanelProps {
  booking: Booking;
  onClose: () => void;
  onStatusChange: (id: number, status: BookingStatus) => void;
  onSaveNotes: (id: number, notes: string) => Promise<boolean>;
  onDelete: (id: number) => void;
  onSendNudge: (id: number) => Promise<NudgeResult>;
}

function DetailRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-primary-700 shrink-0">
        <i className={`${icon} text-sm`}></i>
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
          {label}
        </p>
        <p className="text-sm text-foreground-900 mt-0.5 break-words">{value}</p>
      </div>
    </div>
  );
}

export default function BookingDetailPanel({
  booking,
  onClose,
  onStatusChange,
  onSaveNotes,
  onDelete,
  onSendNudge,
}: BookingDetailPanelProps) {
  const [notes, setNotes] = useState(booking.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [nudgeMsg, setNudgeMsg] = useState<{ text: string; tone: "ok" | "muted" } | null>(null);
  const [guestPhotos, setGuestPhotos] = useState<{ src: string; alt?: string }[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);

  // Match reviews to this booking by guest name and surface the photos the
  // guest attached, so staff can preview the trip before following up.
  useEffect(() => {
    let active = true;
    const guestName = (booking.name || "").trim();
    if (!guestName) {
      setGuestPhotos([]);
      return () => {
        active = false;
      };
    }
    setPhotosLoading(true);
    (async () => {
      try {
        const { data } = await supabase
          .from("reviews")
          .select("photos")
          .ilike("visitor_name", guestName);
        const photos: { src: string; alt?: string }[] = [];
        (data || []).forEach((row: { photos?: { src: string; alt?: string }[] | null }) => {
          (row.photos || []).forEach((photo) => {
            if (photo?.src) photos.push({ src: photo.src, alt: photo.alt });
          });
        });
        if (active) setGuestPhotos(photos);
      } catch {
        if (active) setGuestPhotos([]);
      } finally {
        if (active) setPhotosLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [booking.id, booking.name]);

  const photoPaths = useMemo(() => guestPhotos.map((photo) => photo.src), [guestPhotos]);
  const photoUrlMap = useAssetUrlMap(photoPaths);

  useEffect(() => {
    setNotes(booking.admin_notes || "");
    setSaved(false);
    setConfirmDelete(false);
    setNudgeMsg(null);
  }, [booking.id, booking.admin_notes]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const meta = statusMeta(booking.status);
  const phoneDigits = (booking.phone || "").replace(/[^0-9]/g, "");

  const handleSave = async () => {
    setSaving(true);
    const ok = await onSaveNotes(booking.id, notes);
    setSaving(false);
    if (ok) {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    }
  };

  const handleNudge = async () => {
    setNudging(true);
    setNudgeMsg(null);
    const result = await onSendNudge(booking.id);
    setNudging(false);
    if (result === "sent") {
      setNudgeMsg({ text: "Review nudge sent to the guest.", tone: "ok" });
    } else if (result === "skipped") {
      setNudgeMsg({ text: "Email sending isn't configured yet — connect it to send nudges.", tone: "muted" });
    } else {
      setNudgeMsg({ text: "We couldn't send that nudge. Please try again.", tone: "muted" });
    }
    window.setTimeout(() => setNudgeMsg(null), 6000);
  };

  return (
    <div className="fixed inset-0 z-[110]">
      <div className="absolute inset-0 bg-foreground-950/60" onClick={onClose} aria-hidden="true"></div>

      <aside
        className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-background-50 border-l border-background-200 flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={`Booking from ${booking.name}`}
      >
        <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 border-b border-background-200">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
              Booking #{booking.id}
            </p>
            <h2 className="font-heading text-xl text-foreground-950 font-semibold truncate">
              {booking.name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close booking details"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${meta.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
              {meta.label}
            </span>
            <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-3 py-1.5 rounded-full">
              {typeLabel(booking.booking_type)}
            </span>
          </div>

          <div>
            <label
              htmlFor="booking-status"
              className="block text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2"
            >
              Booking status
            </label>
            <select
              id="booking-status"
              value={booking.status}
              onChange={(event) => onStatusChange(booking.id, event.target.value as BookingStatus)}
              className="w-full bg-background-100 border border-background-300 rounded-md px-3 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              {BOOKING_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STATUS_META[status].label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              href={`mailto:${booking.email}`}
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line"></i> Email
            </a>
            {booking.phone ? (
              <a
                href={`tel:${booking.phone}`}
                className="inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i> Call
              </a>
            ) : null}
            {phoneDigits ? (
              <a
                href={`https://wa.me/${phoneDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i> WhatsApp
              </a>
            ) : null}
          </div>

          {photosLoading ? null : guestPhotos.length > 0 ? (
            <div className="bg-background-100 border border-background-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                  Guest visit photos
                </p>
                <span className="text-[10px] font-bold bg-secondary-100 text-secondary-700 border border-secondary-200 rounded-full px-2 py-0.5">
                  {guestPhotos.length}
                </span>
              </div>
              <p className="text-sm text-foreground-700 mb-3">
                What {booking.name} captured on their visit — preview before you follow up.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {guestPhotos.map((photo, index) => {
                  const url = isStoragePath(photo.src) ? photoUrlMap[photo.src] : photo.src;
                  return (
                    <a
                      key={`${photo.src}-${index}`}
                      href={url || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block aspect-square rounded-md overflow-hidden bg-background-200 border border-background-300"
                    >
                      {url ? (
                        <img
                          src={url}
                          alt={photo.alt || `Visit photo ${index + 1}`}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <span className="w-full h-full flex items-center justify-center text-foreground-400">
                          <i className="ri-image-line text-xl"></i>
                        </span>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 bg-background-100 border border-background-200 rounded-lg px-4 py-3">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 shrink-0">
                <i className="ri-image-2-line text-sm"></i>
              </span>
              <p className="text-sm text-foreground-600">
                No visit photos submitted yet — they&rsquo;ll appear here once this guest shares their experience.
              </p>
            </div>
          )}

          <div className="bg-background-100 border border-background-200 rounded-lg p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-1">
              Guest follow-up
            </p>
            <p className="text-sm text-foreground-700 mb-3">
              Re-send the friendly &ldquo;share your experience&rdquo; email without changing the booking status.
            </p>
            <button
              type="button"
              onClick={handleNudge}
              disabled={nudging || !booking.email}
              className="inline-flex items-center gap-2 bg-secondary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              <i className={nudging ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
              {nudging ? "Sending…" : "Send review nudge"}
            </button>
            {nudgeMsg ? (
              <p
                className={`mt-3 text-xs font-semibold flex items-center gap-1.5 ${
                  nudgeMsg.tone === "ok" ? "text-primary-700" : "text-foreground-500"
                }`}
              >
                <i className={nudgeMsg.tone === "ok" ? "ri-checkbox-circle-fill" : "ri-information-line"}></i>
                {nudgeMsg.text}
              </p>
            ) : null}
          </div>

          <div className="space-y-4">
            <DetailRow icon="ri-mail-line" label="Email" value={booking.email} />
            <DetailRow icon="ri-phone-line" label="Phone" value={booking.phone || "Not provided"} />
            <DetailRow
              icon="ri-bookmark-line"
              label={typeLabel(booking.booking_type)}
              value={booking.subject || "—"}
            />
            <DetailRow
              icon="ri-calendar-line"
              label="Preferred date"
              value={formatBookingDate(booking.preferred_date)}
            />
            <DetailRow
              icon="ri-group-line"
              label="Group size"
              value={booking.group_size ? `${booking.group_size} people` : "Not specified"}
            />
            <DetailRow
              icon="ri-time-line"
              label="Received"
              value={formatDateTime(booking.created_at)}
            />
            {booking.source_page ? (
              <DetailRow icon="ri-links-line" label="Came from" value={booking.source_page} />
            ) : null}
          </div>

          {booking.itinerary ? (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">
                Itinerary
              </p>
              <pre className="bg-background-100 border border-background-200 rounded-md p-4 text-xs text-foreground-800 whitespace-pre-wrap font-body leading-relaxed">
                {booking.itinerary}
              </pre>
            </div>
          ) : null}

          {booking.message ? (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">
                Message
              </p>
              <p className="bg-background-100 border border-background-200 rounded-md p-4 text-sm text-foreground-800 leading-relaxed whitespace-pre-wrap">
                {booking.message}
              </p>
            </div>
          ) : null}

          <div>
            <label
              htmlFor="booking-notes"
              className="block text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2"
            >
              Internal notes
            </label>
            <textarea
              id="booking-notes"
              rows={3}
              maxLength={1000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Add a note for your team — callbacks, quotes, follow-ups…"
              className="w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
            ></textarea>
            <div className="flex items-center gap-3 mt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-secondary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                <i className={`${saving ? "ri-loader-4-line animate-spin" : "ri-save-line"}`}></i>
                {saving ? "Saving…" : "Save note"}
              </button>
              {saved ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700">
                  <i className="ri-checkbox-circle-fill"></i> Saved
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="px-5 md:px-6 py-4 border-t border-background-200">
          {confirmDelete ? (
            <div className="flex items-center gap-3">
              <p className="text-sm text-foreground-700 flex-1">Delete this booking permanently?</p>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-sm font-semibold text-foreground-700 px-3 py-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onDelete(booking.id)}
                className="text-sm font-semibold bg-accent-600 text-background-50 px-4 py-2 rounded-md hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Yes, delete
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
            >
              <i className="ri-delete-bin-line"></i> Delete booking
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}